import { BottomSheetModal } from 'components/Elements';
import React, { createContext, useContext, useEffect, useState, useRef } from 'react';

const LocationContext = createContext(null);

export function LocationProvider({ children }) {
  const [coords, setCoords] = useState(null);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);
  const [sheetVisible, setSheetVisible] = useState(false);
  const userTriggeredRef = useRef(false);
  const hasAttemptedRef = useRef(false);
  const retryRef = useRef(0);
  const MAX_RETRIES = 2;

  const getLocation = () => {
    setStatus('loading');

    if (!navigator.geolocation) {
      setStatus('error');
      setError('Geolocation is not supported on this device.');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        });
        setStatus('ready');
        setSheetVisible(false);
        retryRef.current = 0;
      },
      (err) => {
        console.warn('Geo error:', err);

        // permission denied → show modal
        if (err.code === 1) {
          setStatus('denied');
          setError('Location permission is required.');
          setSheetVisible(true);
          return;
        }

        // POSITION_UNAVAILABLE or TIMEOUT
        if (err.code === 2 || err.code === 3) {
          setStatus('unavailable');
          setError('We couldn’t fetch your location right now.');

          // IMPORTANT: only show modal if user explicitly triggered retry OR first load
          if (userTriggeredRef.current) {
            setSheetVisible(true);
            userTriggeredRef.current = false;
          }

          return;
        }

        setStatus('error');
        setError('Unexpected location error.');
        setSheetVisible(true);
      },
      {
        enableHighAccuracy: false,
        timeout: 15000,
        maximumAge: 30000,
      }
    );
  };

  useEffect(() => {
    if (hasAttemptedRef.current) return;
    hasAttemptedRef.current = true;

    getLocation();
  }, []);

  const requestPermissionAgain = () => {
    userTriggeredRef.current = true;
    setSheetVisible(false);

    setTimeout(() => {
      getLocation();
    }, 300);
  };

  return (
    <LocationContext.Provider value={{ coords, status, error, requestPermissionAgain }}>
      {children}

      <BottomSheetModal
        visible={sheetVisible}
        title="Enable location access"
        onClose={() => setSheetVisible(false)}
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600 leading-relaxed">
            Dejunk uses your location to show items near you, improve search relevance, and
            calculate delivery radius.
          </p>

          <div className="space-y-2">
            <button
              onClick={requestPermissionAgain}
              className="w-full bg-gray-900 text-white py-3 rounded-xl font-semibold"
            >
              Enable location
            </button>

            <button
              onClick={() => setSheetVisible(false)}
              className="w-full bg-gray-100 text-gray-900 py-3 rounded-xl font-semibold"
            >
              Not now
            </button>
          </div>

          <p className="text-xs text-gray-400 text-center">
            You can enable this later in browser settings.
          </p>
        </div>
      </BottomSheetModal>
    </LocationContext.Provider>
  );
}

export const useLocation = () => useContext(LocationContext);
